import React, {useContext, useEffect, useRef, useState} from 'react';
import {Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/CloseRounded";
import './style.scss'
import {AppContext} from "../../../Utils/AppContext";
import SentRouteStopsTable from "../../../SentRouteStopsTable/SentRouteStopsTable";
import AreYouSureDialog from "../../../Utils/AreYouSureDialog/AreYouSureDialog";
import SentRouteMetadataContainer from "../../../../Redux/containers/SentRouteMetadataContainer";
import UndoIcon from '@material-ui/icons/UndoRounded';
import DoneAllIcon from '@material-ui/icons/DoneAllRounded';
import EditIcon from '@material-ui/icons/EditRounded';
import SaveIcon from '@material-ui/icons/SaveRounded';
import CancelRoundedIcon from '@material-ui/icons/CancelRounded';
import {evaluateDeliveryGroupsAction} from "../../../../Redux/actions/deliveryGroupsActions";
import {injectOrdersForGroups} from "../DeliveryGroupsMap";
import createClone from "rfdc";
import Skeleton from "@material-ui/lab/Skeleton";
import useInterval from "../../../Utils/UseInterval";
import Fab from "@material-ui/core/Fab";
import AddIcon from "@material-ui/icons/AddRounded";
import ChooseOrderDialog from "./ChooseOrderDialog/ChooseOrderDialog";

const EditRouteChanges = Object.freeze({"Reorder": 1, "AddOrder": 2, "RemoveOrder": 3, "ChangeMessenger": 4})

export default function SentRouteDialog({
                                            isOpen,
                                            route,
                                            onClose,
                                            readyOrders,
                                            lockedDeliveryGroups,
                                            finishDeliveryGroup,
                                            updateMessengerAvailability,
                                            cancelDeliveryGroupOrders,
                                            cancelDeliveryGroup,
                                            removeOrdersFromLockedGroup,
                                            editDeliveryGroup,
                                            logInfo,
                                            isAutopilot
                                        }) {
    const appContext = useContext(AppContext);
    const [groupColor, setGroupColor] = useState('transparent');
    const [isEdit, setIsEdit] = useState(false);
    const [showBackgroundLoader, setShowBackgroundLoader] = useState(false);
    const [editRoute, setEditRoute] = useState(null);
    const [isSureDialogOpen, setIsSureDialogOpen] = useState(false);
    const [isSureDialogTitleText, setIsSureDialogTitleText] = useState("");
    const [isChooseOrderDialogOpen, setIsChooseOrderDialogOpen] = useState(false);
    const [ordersForChooseDialog, setOrdersForChooseDialog] = useState([]);
    const [isOpenState, setIsOpenState] = useState(false);
    const sureDialogCallbackRef = useRef(null);
    const dialogOpenTimeRef = useRef(null);
    const editRouteStartTimeRef = useRef(null);
    const isEditSavedRef = useRef(false);
    const isEditedRef = useRef(false);
    const editRouteChangesSetRef = useRef(new Set());

    useEffect(() => {
        if (isOpen !== isOpenState) {
            setIsOpenState(isOpen);

            if (isOpen) {
                dialogOpenTimeRef.current = new Date();
                isEditSavedRef.current = false;
                isEditedRef.current = false;
                logInfo("openSentRouteDialog", appContext.bid, {groupId: route.id})
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (editRoute && readyOrders)
            initOrdersForChooseDialog();
    }, [readyOrders, editRoute]);

    useEffect(() => {
        if (route) {
            setGroupColor(appContext.iconColors.getColorByGroupId(route.id).primaryColor);
        } else if (isOpenState) {
            onCloseDialog();
        }
    }, [route]);


    useInterval(() => {
        if (isEdit && editRoute) {
            setEditRoute({...editRoute, timestamp: new Date().toISOString()})
        }
    }, 5000);

    function initOrdersForChooseDialog() {
        const editRouteOrderIdsSet = new Set(editRoute.route.map(stop => stop.order_id));
        setOrdersForChooseDialog(readyOrders.filter(order => !editRouteOrderIdsSet.has(order.id)));
    }

    function onCloseDialog() {
        editRouteChangesSetRef.current.clear();
        setIsChooseOrderDialogOpen(false);
        setOrdersForChooseDialog([]);

        const logMetadata = {
            groupId: route ? route.id : "unknown",
            duration: (new Date() - dialogOpenTimeRef.current) / 1000,
            isWhileEdit: isEdit,
            isEdited: isEditedRef.current,
            isEditSaved: isEditSavedRef.current
        };

        if (isEdit) {
            logMetadata.isAddedOrders = editRouteChangesSetRef.current.has(EditRouteChanges.AddOrder);
            logMetadata.isMessengerChanged = editRouteChangesSetRef.current.has(EditRouteChanges.ChangeMessenger);
            logMetadata.isOrderChanged = editRouteChangesSetRef.current.has(EditRouteChanges.Reorder);
            logMetadata.isRemovedOrders = editRouteChangesSetRef.current.has(EditRouteChanges.RemoveOrder);
        }

        logInfo("closeSentRouteDialog", appContext.bid, logMetadata)
        setIsEdit(false);
        setEditRoute(null);
        setIsOpenState(false);
        isEditSavedRef.current = false;
        isEditedRef.current = false;

        if (onClose)
            onClose();
    }

    function openAreYouSureDialog(titleText, callback) {
        sureDialogCallbackRef.current = callback;

        setIsSureDialogTitleText(titleText);
        setIsSureDialogOpen(true);
    }

    function onNoAreYouSureDialog() {
        sureDialogCallbackRef.current = undefined;

        setIsSureDialogOpen(false);
    }

    function onYesAreYouSureDialog() {
        sureDialogCallbackRef.current();
        sureDialogCallbackRef.current = undefined;

        setIsSureDialogOpen(false);
    }

    async function onFinishDeliveryGroup() {
        await finishDeliveryGroup(route.bid, route.id);
        await updateMessengerAvailability(
            route.bid,
            route.messenger_id,
            true
        );

        if (onClose)
            onClose();
    }

    async function onSaveChanges() {
        const currAbortedOrderIdsSet = new Set(route.aborted_orders.map(o => o.order_id));
        const newAbortedOrderIds = editRoute.aborted_orders
            .filter(o => !currAbortedOrderIdsSet.has(o.order_id))
            .map(o => o.order_id);

        if (newAbortedOrderIds.length > 0)
            await cancelDeliveryGroupOrders(route.bid, route.id, newAbortedOrderIds);

        const lockedOrdersWithGroups = lockedDeliveryGroups.flatMap(lock => lock.order_ids.map(oid => ({
            groupId: lock.group_id,
            orderId: oid
        })));
        const lockedOrdersIndex = Object.assign({}, ...(lockedOrdersWithGroups.map(item => ({[item.orderId]: item.groupId}))));
        const routeLockedOrderIds = editRoute.route
            .filter(stop => stop.order_id in lockedOrdersIndex)
            .map(o => o.order_id);

        for (let lockedOrderId of routeLockedOrderIds) {
            await removeOrdersFromLockedGroup({
                bid: route.bid,
                groupId: lockedOrdersIndex[lockedOrderId],
                orderIds: [lockedOrderId]
            });
        }

        isEditSavedRef.current = true;

        if (editRoute.route.length > 0) {
            if (editRoute.messenger_id !== route.messenger_id) {
                await updateMessengerAvailability(
                    route.bid,
                    route.messenger_id,
                    true
                );
            }

            const editChanges = {
                isAddedOrders: editRouteChangesSetRef.current.has(EditRouteChanges.AddOrder),
                isMessengerChanged: editRouteChangesSetRef.current.has(EditRouteChanges.ChangeMessenger),
                isOrderChanged: editRouteChangesSetRef.current.has(EditRouteChanges.Reorder),
                isRemovedOrders: editRouteChangesSetRef.current.has(EditRouteChanges.RemoveOrder)
            }
            await editDeliveryGroup(editRoute, editChanges);

            setIsEdit(false);
            setEditRoute(null);
            editRouteChangesSetRef.current.clear();
        } else {
            await updateMessengerAvailability(
                route.bid,
                route.messenger_id,
                true
            );

            if (onClose)
                onCloseDialog();
        }
    }

    function prepareRouteForEdit(r) {
        delete r.real_time_finish_time;

        for (let stop of r.route) {
            delete stop.real_time_finish_time
            stop.order.status = "ASSIGNED"
        }

        r.timestamp = new Date().toISOString();
        r.status = "EDIT";
        r.time_till_start = 0;
        r.time_till_start_since_messenger_available = 0;
    }

    function onEditDeliveryGroup() {
        const newIsEdit = !isEdit;

        // If enable edit
        if (newIsEdit) {
            const rfdcClone = createClone();
            const clonedRoute = rfdcClone(route);
            prepareRouteForEdit(clonedRoute);
            setEditRoute(clonedRoute);
            logInfo("startEditSentRoute", appContext.bid, {groupId: route.id});
            editRouteStartTimeRef.current = new Date();
            isEditedRef.current = true;
        } else {
            const logMetadata = {
                groupId: route.id,
                duration: (new Date() - editRouteStartTimeRef.current) / 1000,
                isAddedOrders: editRouteChangesSetRef.current.has(EditRouteChanges.AddOrder),
                isMessengerChanged: editRouteChangesSetRef.current.has(EditRouteChanges.ChangeMessenger),
                isOrderChanged: editRouteChangesSetRef.current.has(EditRouteChanges.Reorder),
                isRemovedOrders: editRouteChangesSetRef.current.has(EditRouteChanges.RemoveOrder)
            }
            logInfo("cancelEditSentRoute", appContext.bid, logMetadata);
            setEditRoute(null);
            editRouteChangesSetRef.current.clear();
        }

        setIsEdit(newIsEdit);
    }

    async function onCancelDeliveryGroup() {
        await cancelDeliveryGroup(route.bid, route.id);
        await updateMessengerAvailability(
            route.bid,
            route.messenger_id,
            true
        );

        if (onClose)
            onClose();
    }

    function onChangeMessenger(messengerId) {
        setEditRoute({...editRoute, messenger_id: messengerId});
        editRouteChangesSetRef.current.add(EditRouteChanges.ChangeMessenger);
    }

    async function evaluateRouteForOrders(isOrdered, routeId, orders, messengerId, abortedOrders) {
        const groupings = {
            groups: [{
                groupId: 'on-demand-' + routeId,
                orderIds: orders.map(order => order.id),
                isLocked: true,
                isOrdered: isOrdered
            }],
            vehicles: [{"id": messengerId}],
            standalone: true
        };

        const vrpResult = await evaluateDeliveryGroupsAction(appContext.bid, groupings).payload();

        const routeResult = vrpResult.deliveryGroups.delivery_groups[0];
        const routeResultInjected = injectOrdersForGroups([routeResult], orders)[0];
        routeResultInjected.bid = appContext.bid;
        routeResultInjected.id = routeResultInjected.id.replace("on-demand-", "");
        if (abortedOrders)
            routeResultInjected.aborted_orders = abortedOrders;

        return routeResultInjected;
    }

    async function onReorderRoute(newStopsOrder) {
        setShowBackgroundLoader(true);

        const routeResult = await evaluateRouteForOrders(
            true,
            editRoute.id,
            newStopsOrder.map(newStop => editRoute.route.find(stop => stop.order_id === newStop.order_id).order),
            editRoute.messenger_id,
            editRoute.aborted_orders);

        setEditRoute(routeResult);
        editRouteChangesSetRef.current.add(EditRouteChanges.Reorder);

        setShowBackgroundLoader(false);
    }

    async function onRemoveStopClicked(orderId) {
        const foundStopIndex = editRoute.route.findIndex(s => s.order_id === orderId);

        if (foundStopIndex !== -1) {
            const foundStop = editRoute.route[foundStopIndex];

            const newRouteStops = [...editRoute.route];
            newRouteStops.splice(foundStopIndex, 1);

            const newAbortedStops = [...editRoute.aborted_orders];
            const isStopInOriginalRoute = route.route.some(s => s.order_id === orderId);

            if (isStopInOriginalRoute) {
                newAbortedStops.push(foundStop);
                editRouteChangesSetRef.current.add(EditRouteChanges.RemoveOrder);
            }

            if (newRouteStops.length > 0) {
                setShowBackgroundLoader(true);

                const routeResult = await evaluateRouteForOrders(
                    true,
                    editRoute.id,
                    newRouteStops.map(s => s.order),
                    editRoute.messenger_id,
                    newAbortedStops);

                setEditRoute(routeResult);

                setShowBackgroundLoader(false);
            } else
                setEditRoute({...editRoute, route: newRouteStops, aborted_orders: newAbortedStops});
        }
    }

    function onAddOrderClick() {
        setIsChooseOrderDialogOpen(true);
    }

    async function onChooseOrders(chosenOrders) {
        setIsChooseOrderDialogOpen(false);

        if (chosenOrders.length > 0) {
            const chosenOrder = chosenOrders[0];

            setShowBackgroundLoader(true);

            const routeOrders = editRoute.route.map(s => s.order);
            routeOrders.push(chosenOrder);

            const newAbortedOrders = editRoute.aborted_orders.filter(order => order.order_id !== chosenOrder.id);

            const routeResult = await evaluateRouteForOrders(
                false,
                editRoute.id,
                routeOrders,
                editRoute.messenger_id,
                newAbortedOrders);

            setEditRoute(routeResult);
            editRouteChangesSetRef.current.add(EditRouteChanges.AddOrder);

            setShowBackgroundLoader(false);
        }

        setOrdersForChooseDialog([]);
    }

    function onChooseOrderDialogClose() {
        setIsChooseOrderDialogOpen(false);
    }

    const relevantRoute = isEdit ? editRoute : route;

    const isCancelPossible = !isAutopilot || (route && (route.status === "SEARCHING_FOR_DRIVER" || route.status === "ASSIGNED"));
    const isEditPossible = !isAutopilot;

    return (
        <React.Fragment>
            <Dialog className={"sent-route-dialog " + (appContext.direction === "rtl" ? "dialog-rtl" : "dialog-ltr")}
                    open={isOpenState}
                    onClose={onCloseDialog}
                    PaperProps={{className: "sent-route-dialog-paper"}}>
                <div className="sent-route-dialog-content-wrapper" style={{border: `6px solid ${groupColor}`}}>
                    <DialogTitle id="draggable-sent-dialog-title">
                        {
                            !route ?
                                null
                                :
                                <SentRouteMetadataContainer isEdit={isEdit}
                                                            route={relevantRoute}
                                                            onChangeMessenger={onChangeMessenger}
                                                            additionalMessengerId={route.messenger_id}/>
                        }
                    </DialogTitle>
                    <DialogContent dividers={true}
                                   className="dialog-content">
                        {
                            !route ?
                                null
                                :
                                <div className="route">
                                    {
                                        showBackgroundLoader ?
                                            <div className="background-loader">
                                                <Skeleton animation="wave"/>
                                            </div>
                                            :
                                            null
                                    }
                                    {
                                        !relevantRoute.route.length && isEdit ?
                                            <div className="route-empty-container">
                                                <div className="route-empty-text-1">המסלול ריק מהזמנות</div>
                                                <div className="route-empty-text-2">אם תשמור, המסלול יתבטל</div>
                                            </div>
                                            :
                                            <SentRouteStopsTable isEdit={isEdit} route={relevantRoute}
                                                                 onReorder={onReorderRoute}
                                                                 onRemoveStopClicked={onRemoveStopClicked}/>
                                    }
                                    {
                                        isEdit ?
                                            <div className="add-order-container">
                                                <Tooltip disableFocusListener={true} arrow
                                                         enterTouchDelay={0}
                                                         title={ordersForChooseDialog.length === 0 ? "אין הזמנות להוסיף" : "הוסף הזמנה"}>
                                                <span>
                                                    <Fab
                                                        className={"add-order-button" + (ordersForChooseDialog.length === 0 ? " add-order-button-disabled" : "")}
                                                        disabled={ordersForChooseDialog.length === 0}
                                                        aria-label="Add"
                                                        size="small"
                                                        onClick={onAddOrderClick}>
                                                        <AddIcon/>
                                                    </Fab>
                                                </span>
                                                </Tooltip>
                                            </div>
                                            :
                                            null
                                    }
                                </div>
                        }
                    </DialogContent>
                    <DialogActions className="dialog-actions">
                        <IconButton className="close-button"
                                    onClick={onCloseDialog}>
                            <CloseIcon fontSize="medium"/>
                        </IconButton>
                        {
                            isEdit ?
                                <React.Fragment>
                                    <Tooltip disableFocusListener={true} arrow
                                             title="בטל עריכה">
                                        <IconButton className="edit-group-button"
                                                    onClick={onEditDeliveryGroup}>
                                            <EditIcon/>
                                            <div className="cancel-edit-inner-icon">
                                                <CancelRoundedIcon/>
                                            </div>
                                        </IconButton>
                                    </Tooltip>
                                    <IconButton style={{"visibility": "hidden"}}>
                                        <SaveIcon/>
                                    </IconButton>
                                    <Tooltip disableFocusListener={true} arrow
                                             title="שמור שינויים">
                                        <IconButton className="save-changes-button"
                                                    onClick={() => openAreYouSureDialog("האם אתה בטוח שברצונך לשמור את המסלול?", () => onSaveChanges())}>
                                            <SaveIcon/>
                                        </IconButton>
                                    </Tooltip>
                                </React.Fragment>
                                :
                                <React.Fragment>
                                    {
                                        isEditPossible ?
                                            <Tooltip disableFocusListener={true} arrow
                                                     title="ערוך מסלול">
                                                <IconButton className="edit-group-button"
                                                            onClick={onEditDeliveryGroup}>
                                                    <EditIcon/>
                                                </IconButton>
                                            </Tooltip>
                                            :
                                            null
                                    }
                                    {
                                        isCancelPossible ?
                                            <Tooltip disableFocusListener={true} arrow
                                                     title="בטל מסלול">
                                                <IconButton className="cancel-group-button"
                                                            onClick={() => openAreYouSureDialog("האם אתה בטוח שברצונך לבטל את המסלול?", () => onCancelDeliveryGroup())}>
                                                    <UndoIcon/>
                                                </IconButton>
                                            </Tooltip>
                                            :
                                            null
                                    }
                                    <Tooltip disableFocusListener={true} arrow
                                             title="סיים מסלול">
                                        <IconButton className="finish-group-button"
                                                    onClick={() => openAreYouSureDialog("האם אתה בטוח שברצונך לסיים את המסלול?", () => onFinishDeliveryGroup())}>
                                            <DoneAllIcon/>
                                        </IconButton>
                                    </Tooltip>
                                </React.Fragment>
                        }
                    </DialogActions>
                </div>
            </Dialog>
            <AreYouSureDialog titleText={isSureDialogTitleText}
                              open={isSureDialogOpen}
                              onYes={onYesAreYouSureDialog}
                              onNo={onNoAreYouSureDialog}/>
            {
                isOpen ?
                    <ChooseOrderDialog titleText={"בחר הזמנה להוסיף למסלול"}
                                       orders={ordersForChooseDialog}
                                       buttonTooltipText={"הוסף"}
                                       isMultiple={false}
                                       isOpen={isChooseOrderDialogOpen}
                                       onClose={onChooseOrderDialogClose}
                                       onChoose={onChooseOrders}/>
                    :
                    null
            }
        </React.Fragment>
    );
}